import { z } from 'zod';

import { OutboundRecordReferenceSchema } from '../outbound/outbound-record-reference.schema.js';

import {
  HoldingUnitAssistedMatchCandidateSchema,
  HoldingUnitInventorySummaryItemSchema,
  HoldingUnitLifecycleStatusSchema,
  HoldingUnitLoadSessionDiscrepancyReviewSchema,
  HoldingUnitLoadSessionMatchCandidateSchema,
  HoldingUnitLoadSessionGroupClaimStatusSchema,
  HoldingUnitLoadSessionGroupTotalsSchema,
  HoldingUnitLoadSessionItemSchema,
  HoldingUnitLoadSessionSchema,
  HoldingUnitLoadSessionMaterialGroupSchema,
  HoldingUnitLoadSessionStatusSchema,
  HoldingUnitReservationStateSchema,
  HoldingUnitSnapshotSchema,
} from './holding-units.schema.js';
import { FixedIdAssetRegistryItemSchema } from './fixed-id-assets.schema.js';
import {
  holdingUnitProjectionCheckpointEntityTypeValues,
  holdingUnitProjectionCheckpointStatusValues,
} from './holding-units-queries.dto.js';
import type {
  BatchGetHoldingUnitCurrentStatesItem,
  BatchGetHoldingUnitCurrentStatesRequest,
  BatchGetHoldingUnitCurrentStatesResponse,
  GetAssistedMatchingCandidatesRequest,
  GetAssistedMatchingCandidatesResponse,
  GetHoldingUnitProjectionCheckpointRequest,
  GetHoldingUnitProjectionCheckpointResponse,
  GetHoldingUnitProjectionCheckpointStatusRequest,
  GetHoldingUnitProjectionCheckpointStatusResponse,
  GetHoldingUnitLoadSessionDetailRequest,
  GetHoldingUnitLoadSessionDetailResponse,
  GetHoldingUnitLoadSessionMatchCandidatesRequest,
  GetHoldingUnitLoadSessionMatchCandidatesResponse,
  GetHoldingUnitLoadSessionShipmentSeedRequest,
  GetHoldingUnitLoadSessionShipmentSeedResponse,
  ListHoldingUnitLoadSessionAvailableUnitsRequest,
  ListHoldingUnitLoadSessionAvailableUnitsResponse,
  HoldingUnitLoadSessionShipmentEvidenceOption,
  HoldingUnitLoadSessionShipmentSeed,
  HoldingUnitLoadSessionGroupDetail,
  ListHoldingUnitLoadSessionShipmentEvidenceOptionsRequest,
  ListHoldingUnitLoadSessionShipmentEvidenceOptionsResponse,
  GetHoldingUnitReservationStateRequest,
  GetHoldingUnitReservationStateResponse,
  GetHoldingUnitSnapshotRequest,
  GetHoldingUnitSnapshotResponse,
  HoldingUnitProjectionCheckpointDto,
  HoldingUnitProjectionCheckpointStatusCounts,
  HoldingUnitProjectionCheckpointStatusSample,
  HoldingUnitProjectionWorkerHealth,
  ResolveHoldingUnitByIdentifierRequest,
  ResolveHoldingUnitByIdentifierResponse,
  GetInventoryByYardRequest,
  GetInventoryByYardResponse,
  InventoryByYardItem,
  InventoryByYardMaterialItem,
  GetInventorySummaryByMaterialAndStatusRequest,
  GetInventorySummaryByMaterialAndStatusResponse,
  ListHoldingUnitLoadSessionsRequest,
  ListHoldingUnitLoadSessionsResponse,
  ListHoldingUnitsRequest,
  ListHoldingUnitsResponse,
} from './holding-units-queries.dto.js';

const timestampSchema = z.iso.datetime();
const dateSchema = z.iso.date();
const HoldingUnitsListModeSchema = z.enum(['work_queue']);
const HoldingUnitsNetStateSchema = z.enum(['positive', 'zero', 'all']);
const HoldingUnitsWorkQueueStatusModeSchema = z.enum(['queue', 'open', 'ready']);
const HoldingUnitsSearchTextSchema = z.string().trim().min(1).max(64);
export const HoldingUnitProjectionCheckpointEntityTypeSchema = z.enum(
  holdingUnitProjectionCheckpointEntityTypeValues,
);
export const HoldingUnitProjectionCheckpointStatusSchema = z.enum(
  holdingUnitProjectionCheckpointStatusValues,
);

export const HoldingUnitProjectionCheckpointStatusCountsSchema: z.ZodType<HoldingUnitProjectionCheckpointStatusCounts> =
  z.object({
    pending: z.number().int().min(0),
    processing: z.number().int().min(0),
    current: z.number().int().min(0),
    failed: z.number().int().min(0),
    dead_letter: z.number().int().min(0),
  });

export const HoldingUnitProjectionCheckpointDtoSchema: z.ZodType<HoldingUnitProjectionCheckpointDto> =
  z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    entityType: HoldingUnitProjectionCheckpointEntityTypeSchema,
    entityId: z.uuid(),
    projectedVersion: z.number().int().min(0),
    targetVersion: z.number().int().min(0),
    status: HoldingUnitProjectionCheckpointStatusSchema,
    attempts: z.number().int().min(0),
    lastErrorCode: z.string().nullable(),
    lastErrorMessage: z.string().nullable(),
    lastErrorStage: z.string().nullable(),
    lastErrorAt: timestampSchema.nullable(),
    nextAttemptAt: timestampSchema.nullable(),
    lockedBy: z.string().nullable(),
    lockedAt: timestampSchema.nullable(),
    lockExpiresAt: timestampSchema.nullable(),
    lastOuterSyncEventId: z.string().nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  });

export const HoldingUnitProjectionWorkerHealthSchema: z.ZodType<HoldingUnitProjectionWorkerHealth> =
  z.object({
    enabled: z.boolean(),
    disabledByEnv: z.boolean(),
    inFlight: z.boolean(),
    intervalMs: z.number().int().positive(),
    maxCheckpointsPerTick: z.number().int().positive(),
    lockTtlMs: z.number().int().positive(),
    startedAt: timestampSchema.nullable(),
    lastTickStartedAt: timestampSchema.nullable(),
    lastTickFinishedAt: timestampSchema.nullable(),
    lastWorkerRunId: z.string().nullable(),
    lastClaimedAt: timestampSchema.nullable(),
    lastClaimedCount: z.number().int().min(0),
    lastErrorAt: timestampSchema.nullable(),
    lastErrorMessage: z.string().nullable(),
  });

export const GetHoldingUnitProjectionCheckpointStatusRequestSchema: z.ZodType<GetHoldingUnitProjectionCheckpointStatusRequest> =
  z.object({
    companyId: z.uuid(),
    entityType: HoldingUnitProjectionCheckpointEntityTypeSchema.optional(),
    status: HoldingUnitProjectionCheckpointStatusSchema.optional(),
    includeCurrent: z.boolean().optional(),
    limit: z.number().int().positive().max(200).optional(),
  });

export const HoldingUnitProjectionCheckpointStatusSampleSchema: z.ZodType<HoldingUnitProjectionCheckpointStatusSample> =
  z.object({
    checkpoint: HoldingUnitProjectionCheckpointDtoSchema,
    lag: z.number().int().min(0),
    ready: z.boolean(),
    canRequeue: z.boolean(),
  });

export const GetHoldingUnitProjectionCheckpointStatusResponseSchema: z.ZodType<GetHoldingUnitProjectionCheckpointStatusResponse> =
  z.object({
    generatedAt: timestampSchema,
    ready: z.boolean(),
    countsByStatus: HoldingUnitProjectionCheckpointStatusCountsSchema,
    countsByEntityTypeAndStatus: z.object({
      holdingUnit: HoldingUnitProjectionCheckpointStatusCountsSchema,
      loadSession: HoldingUnitProjectionCheckpointStatusCountsSchema,
      fixedIdAsset: HoldingUnitProjectionCheckpointStatusCountsSchema,
    }),
    laggingCount: z.number().int().min(0),
    dueNowCount: z.number().int().min(0),
    scheduledRetryCount: z.number().int().min(0),
    staleProcessingCount: z.number().int().min(0),
    oldestPendingAt: timestampSchema.nullable(),
    oldestFailedAt: timestampSchema.nullable(),
    oldestDeadLetterAt: timestampSchema.nullable(),
    sampleNonCurrentCheckpoints: z.array(HoldingUnitProjectionCheckpointStatusSampleSchema),
    workerHealth: HoldingUnitProjectionWorkerHealthSchema.nullable(),
  });

export const GetHoldingUnitProjectionCheckpointRequestSchema: z.ZodType<GetHoldingUnitProjectionCheckpointRequest> =
  z.object({
    companyId: z.uuid(),
    entityType: HoldingUnitProjectionCheckpointEntityTypeSchema,
    entityId: z.uuid(),
  });

export const GetHoldingUnitProjectionCheckpointResponseSchema: z.ZodType<GetHoldingUnitProjectionCheckpointResponse> =
  z.object({
    generatedAt: timestampSchema,
    companyId: z.uuid(),
    entityType: HoldingUnitProjectionCheckpointEntityTypeSchema,
    entityId: z.uuid(),
    checkpoint: HoldingUnitProjectionCheckpointDtoSchema.nullable(),
    projectedVersion: z.number().int().min(0),
    targetVersion: z.number().int().min(0),
    lag: z.number().int().min(0),
    latestEventStreamVersion: z.number().int().min(0),
    projectionReadModelVersion: z.number().int().min(0),
    ready: z.boolean(),
    retry: z.object({
      attempts: z.number().int().min(0),
      nextAttemptAt: timestampSchema.nullable(),
      lastErrorCode: z.string().nullable(),
      lastErrorMessage: z.string().nullable(),
      lastErrorStage: z.string().nullable(),
      lastErrorAt: timestampSchema.nullable(),
    }),
    lock: z.object({
      lockedBy: z.string().nullable(),
      lockedAt: timestampSchema.nullable(),
      lockExpiresAt: timestampSchema.nullable(),
      stale: z.boolean(),
    }),
    canRequeue: z.boolean(),
  });

export const GetHoldingUnitSnapshotRequestSchema: z.ZodType<GetHoldingUnitSnapshotRequest> =
  z.object({
    companyId: z.uuid(),
    unitId: z.uuid(),
  });

export const GetHoldingUnitSnapshotResponseSchema: z.ZodType<GetHoldingUnitSnapshotResponse> =
  z.object({
    unit: HoldingUnitSnapshotSchema,
  });

export const BatchGetHoldingUnitCurrentStatesRequestSchema: z.ZodType<BatchGetHoldingUnitCurrentStatesRequest> =
  z
    .object({
      companyId: z.uuid(),
      unitIds: z.array(z.uuid()).min(1).max(2_000),
    })
    .strict()
    .superRefine((value, ctx) => {
      const seenUnitIds = new Set<string>();
      value.unitIds.forEach((unitId, index) => {
        if (seenUnitIds.has(unitId)) {
          ctx.addIssue({
            code: 'custom',
            message: 'Duplicate unit ids are not allowed.',
            path: ['unitIds', index],
          });
        }
        seenUnitIds.add(unitId);
      });
    });

export const BatchGetHoldingUnitCurrentStatesItemSchema: z.ZodType<BatchGetHoldingUnitCurrentStatesItem> =
  z
    .object({
      snapshot: HoldingUnitSnapshotSchema,
      reservation: HoldingUnitReservationStateSchema.nullable(),
    })
    .strict();

export const BatchGetHoldingUnitCurrentStatesResponseSchema: z.ZodType<BatchGetHoldingUnitCurrentStatesResponse> =
  z
    .object({
      items: z.array(BatchGetHoldingUnitCurrentStatesItemSchema),
    })
    .strict();

const holdingUnitIdentifierValueSchema = z.string().trim().min(1).max(64);

export const ResolveHoldingUnitByIdentifierRequestSchema: z.ZodType<ResolveHoldingUnitByIdentifierRequest> =
  z.object({
    companyId: z.uuid(),
    identifier: holdingUnitIdentifierValueSchema,
  });

export const ResolveHoldingUnitByIdentifierResponseSchema: z.ZodType<ResolveHoldingUnitByIdentifierResponse> =
  z.discriminatedUnion('resultType', [
    z.object({
      resultType: z.literal('cycle'),
      unit: HoldingUnitSnapshotSchema,
      asset: FixedIdAssetRegistryItemSchema.nullable(),
    }),
    z.object({
      resultType: z.literal('fixedIdAsset'),
      unit: z.null(),
      asset: FixedIdAssetRegistryItemSchema,
    }),
    z.object({
      resultType: z.literal('notFound'),
      unit: z.null(),
      asset: z.null(),
    }),
  ]);

export const HoldingUnitsWorkQueueCursorSchema = z
  .string()
  .trim()
  .regex(
    /^v1\.[01]\.\d{1,16}\.[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
    'Invalid Holding Units work-queue cursor.',
  )
  .refine((value) => {
    const timestamp = Number(value.split('.')[2]);
    return Number.isSafeInteger(timestamp) && !Number.isNaN(new Date(timestamp).getTime());
  }, 'Invalid Holding Units work-queue cursor timestamp.');

export const ListHoldingUnitsRequestSchema: z.ZodType<ListHoldingUnitsRequest> = z
  .object({
    companyId: z.uuid(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    lifecycleStatus: HoldingUnitLifecycleStatusSchema.optional(),
    includeSuperseded: z.boolean().optional(),
    searchText: HoldingUnitsSearchTextSchema.optional(),
    yardLocationId: z.uuid().nullable().optional(),
    materialId: z.uuid().optional(),
    unitProfileId: z.uuid().optional(),
    mode: HoldingUnitsListModeSchema.optional(),
    netState: HoldingUnitsNetStateSchema.optional(),
    approxNetLb: z.number().nonnegative().optional(),
    netToleranceLb: z.number().nonnegative().optional(),
    approxGrossLb: z.number().nonnegative().optional(),
    grossToleranceLb: z.number().nonnegative().optional(),
    statusMode: HoldingUnitsWorkQueueStatusModeSchema.optional(),
    limit: z.number().int().positive().max(200).optional(),
    cursor: HoldingUnitsWorkQueueCursorSchema.optional(),
  })
  .superRefine((value, ctx) => {
    const hasNetApproximateFilter =
      value.approxNetLb !== undefined || value.netToleranceLb !== undefined;
    const hasGrossApproximateFilter =
      value.approxGrossLb !== undefined || value.grossToleranceLb !== undefined;

    if (hasNetApproximateFilter && hasGrossApproximateFilter) {
      ctx.addIssue({
        code: 'custom',
        message: 'Approximate gross and approximate net filters cannot be combined.',
        path: ['approxGrossLb'],
      });
    }

    if (value.approxNetLb !== undefined && value.netToleranceLb === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'netToleranceLb is required when approxNetLb is provided.',
        path: ['netToleranceLb'],
      });
    }

    if (value.netToleranceLb !== undefined && value.approxNetLb === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'approxNetLb is required when netToleranceLb is provided.',
        path: ['approxNetLb'],
      });
    }

    if (value.approxGrossLb !== undefined && value.grossToleranceLb === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'grossToleranceLb is required when approxGrossLb is provided.',
        path: ['grossToleranceLb'],
      });
    }

    if (value.grossToleranceLb !== undefined && value.approxGrossLb === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'approxGrossLb is required when grossToleranceLb is provided.',
        path: ['approxGrossLb'],
      });
    }

    if (value.cursor !== undefined && value.mode !== 'work_queue') {
      ctx.addIssue({
        code: 'custom',
        message: 'cursor is only supported for work_queue mode.',
        path: ['cursor'],
      });
    }

    if (value.cursor !== undefined) {
      const cursorNetRank = Number(value.cursor.split('.')[1]);
      const requestedNetState = value.netState ?? 'positive';
      if (
        (requestedNetState === 'positive' && cursorNetRank !== 0) ||
        (requestedNetState === 'zero' && cursorNetRank !== 1)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'cursor net group does not match the requested netState.',
          path: ['cursor'],
        });
      }
    }
  });

export const ListHoldingUnitsResponseSchema: z.ZodType<ListHoldingUnitsResponse> = z.object({
  items: z.array(HoldingUnitSnapshotSchema),
  truncated: z.boolean().optional(),
  limit: z.number().int().positive().max(200).optional(),
  nextCursor: HoldingUnitsWorkQueueCursorSchema.optional(),
  zeroNetCount: z.number().int().nonnegative().optional(),
});

export const GetHoldingUnitReservationStateRequestSchema: z.ZodType<GetHoldingUnitReservationStateRequest> =
  z.object({
    companyId: z.uuid(),
    unitId: z.uuid(),
  });

export const GetHoldingUnitReservationStateResponseSchema: z.ZodType<GetHoldingUnitReservationStateResponse> =
  z.object({
    reservation: HoldingUnitReservationStateSchema.nullable(),
  });

export const ListHoldingUnitLoadSessionsRequestSchema: z.ZodType<ListHoldingUnitLoadSessionsRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    status: HoldingUnitLoadSessionStatusSchema.optional(),
    from: timestampSchema.optional(),
    to: timestampSchema.optional(),
  });

export const ListHoldingUnitLoadSessionsResponseSchema: z.ZodType<ListHoldingUnitLoadSessionsResponse> =
  z.object({
    items: z.array(HoldingUnitLoadSessionSchema),
  });

export const GetHoldingUnitLoadSessionDetailRequestSchema: z.ZodType<GetHoldingUnitLoadSessionDetailRequest> =
  z
    .object({
      companyId: z.uuid(),
      loadSessionId: z.uuid(),
    })
    .strict();

export const HoldingUnitLoadSessionGroupDetailSchema: z.ZodType<HoldingUnitLoadSessionGroupDetail> =
  z
    .object({
      group: HoldingUnitLoadSessionMaterialGroupSchema,
      totals: HoldingUnitLoadSessionGroupTotalsSchema,
      claimStatus: HoldingUnitLoadSessionGroupClaimStatusSchema,
      claimedByOutboundRecord: OutboundRecordReferenceSchema.nullable(),
      isSelectableForShipment: z.boolean(),
      items: z.array(HoldingUnitLoadSessionItemSchema),
      discrepancyReview: HoldingUnitLoadSessionDiscrepancyReviewSchema,
    })
    .strict();

export const GetHoldingUnitLoadSessionDetailResponseSchema: z.ZodType<GetHoldingUnitLoadSessionDetailResponse> =
  z
    .object({
      loadSession: HoldingUnitLoadSessionSchema,
      groups: z.array(HoldingUnitLoadSessionGroupDetailSchema),
    })
    .strict();

export const ListHoldingUnitLoadSessionShipmentEvidenceOptionsRequestSchema: z.ZodType<ListHoldingUnitLoadSessionShipmentEvidenceOptionsRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    materialId: z.uuid().optional(),
    requireMaterialMatch: z.boolean().optional(),
    limit: z.number().int().positive().max(100).optional(),
  });

export const HoldingUnitLoadSessionShipmentEvidenceOptionSchema: z.ZodType<HoldingUnitLoadSessionShipmentEvidenceOption> =
  z.object({
    loadSession: HoldingUnitLoadSessionSchema,
    groups: z.array(HoldingUnitLoadSessionGroupDetailSchema),
  });

export const ListHoldingUnitLoadSessionShipmentEvidenceOptionsResponseSchema: z.ZodType<ListHoldingUnitLoadSessionShipmentEvidenceOptionsResponse> =
  z.object({
    items: z.array(HoldingUnitLoadSessionShipmentEvidenceOptionSchema),
    truncated: z.boolean(),
    limit: z.number().int().positive().max(100),
  });

export const ListHoldingUnitLoadSessionAvailableUnitsRequestSchema: z.ZodType<ListHoldingUnitLoadSessionAvailableUnitsRequest> =
  z.object({
    companyId: z.uuid(),
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
  });

export const ListHoldingUnitLoadSessionAvailableUnitsResponseSchema: z.ZodType<ListHoldingUnitLoadSessionAvailableUnitsResponse> =
  z.object({
    availableUnits: z.array(HoldingUnitSnapshotSchema),
    eligibleOpenUnits: z.array(HoldingUnitSnapshotSchema),
  });

export const GetHoldingUnitLoadSessionShipmentSeedRequestSchema: z.ZodType<GetHoldingUnitLoadSessionShipmentSeedRequest> =
  z.object({
    companyId: z.uuid(),
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
  });

export const HoldingUnitLoadSessionShipmentSeedSchema: z.ZodType<HoldingUnitLoadSessionShipmentSeed> =
  z
    .object({
      loadSessionId: z.uuid(),
      loadSessionName: z.string().trim().min(1).max(80).nullable(),
      groupId: z.uuid(),
      materialId: z.uuid(),
      targetLb: z.number().min(0),
      locationId: z.uuid(),
      storeId: z.uuid(),
    })
    .strict();

export const GetHoldingUnitLoadSessionShipmentSeedResponseSchema: z.ZodType<GetHoldingUnitLoadSessionShipmentSeedResponse> =
  HoldingUnitLoadSessionShipmentSeedSchema;

export const GetInventorySummaryByMaterialAndStatusRequestSchema: z.ZodType<GetInventorySummaryByMaterialAndStatusRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    materialId: z.uuid().optional(),
    lifecycleStatus: HoldingUnitLifecycleStatusSchema.optional(),
    businessDate: dateSchema.optional(),
  });

export const GetInventorySummaryByMaterialAndStatusResponseSchema: z.ZodType<GetInventorySummaryByMaterialAndStatusResponse> =
  z.object({
    items: z.array(HoldingUnitInventorySummaryItemSchema),
  });

const InventoryByYardMaterialItemSchema: z.ZodType<InventoryByYardMaterialItem> = z.object({
  materialId: z.uuid().nullable(),
  totalNetLb: z.number().min(0),
  unitCount: z.number().int().min(0),
  positiveUnitCount: z.number().int().min(0).optional(),
  emptyUnitCount: z.number().int().min(0).optional(),
});

export const InventoryByYardItemSchema: z.ZodType<InventoryByYardItem> = z.object({
  yardLocationId: z.uuid().nullable(),
  yardName: z.string().trim().min(1).nullable(),
  yardCode: z.string().trim().min(1).nullable(),
  isYardActive: z.boolean().nullable(),
  totalNetLb: z.number().min(0),
  unitCount: z.number().int().min(0),
  positiveUnitCount: z.number().int().min(0).optional(),
  emptyUnitCount: z.number().int().min(0).optional(),
  byMaterial: z.array(InventoryByYardMaterialItemSchema).optional(),
});

export const GetInventoryByYardRequestSchema: z.ZodType<GetInventoryByYardRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid().optional(),
  locationId: z.uuid().optional(),
  includeInactiveYards: z.boolean().optional(),
  lifecycleStatuses: z.array(HoldingUnitLifecycleStatusSchema).min(1).optional(),
  materialId: z.uuid().optional(),
  groupByMaterial: z.boolean().optional(),
});

export const GetInventoryByYardResponseSchema: z.ZodType<GetInventoryByYardResponse> = z.object({
  items: z.array(InventoryByYardItemSchema),
  generatedAt: timestampSchema,
});

export const GetAssistedMatchingCandidatesRequestSchema: z.ZodType<GetAssistedMatchingCandidatesRequest> =
  z.object({
    companyId: z.uuid(),
    storeId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    materialId: z.uuid(),
    lifecycleStatus: HoldingUnitLifecycleStatusSchema,
    grossLb: z.number().positive(),
    toleranceProfileKey: z.string().trim().min(1).max(50),
  });

export const GetAssistedMatchingCandidatesResponseSchema: z.ZodType<GetAssistedMatchingCandidatesResponse> =
  z.object({
    items: z.array(HoldingUnitAssistedMatchCandidateSchema),
  });

export const GetHoldingUnitLoadSessionMatchCandidatesRequestSchema: z.ZodType<GetHoldingUnitLoadSessionMatchCandidatesRequest> =
  z.object({
    companyId: z.uuid(),
    loadSessionId: z.uuid(),
    groupId: z.uuid(),
    grossLb: z.number().positive(),
    toleranceProfileKey: z.string().trim().min(1).max(50).optional(),
  });

export const GetHoldingUnitLoadSessionMatchCandidatesResponseSchema: z.ZodType<GetHoldingUnitLoadSessionMatchCandidatesResponse> =
  z.object({
    autoCandidate: HoldingUnitLoadSessionMatchCandidateSchema.nullable(),
    candidates: z.array(HoldingUnitLoadSessionMatchCandidateSchema),
  });
